/**
 * Formats a user's display name from firstName and lastName.
 *
 * Handles the case where Azure SSO stores names in "LastName, FirstName" format
 * by detecting a comma in the firstName field and swapping the order.
 *
 * Examples:
 * - firstName: "John", lastName: "Smith" → "John Smith"
 * - firstName: "Smith,", lastName: "John" → "John Smith"
 * - firstName: "Smith, ", lastName: "John" → "John Smith"
 *
 * @param firstName - The first name field (may contain "LastName," due to SSO)
 * @param lastName - The last name field (may contain "FirstName" due to SSO)
 * @returns The properly formatted full name
 */
export const formatDisplayName = (
  firstName: string | undefined | null,
  lastName: string | undefined | null,
): string => {
  const first = firstName?.trim() ?? '';
  const last = lastName?.trim() ?? '';

  // Check if firstName ends with a comma (indicating "LastName," format from Azure SSO)
  if (first.endsWith(',')) {
    // Remove the trailing comma from firstName (which is actually the last name)
    const actualLastName = first.slice(0, -1).trim();
    // lastName contains the actual first name
    const actualFirstName = last;
    return `${actualFirstName} ${actualLastName}`.trim();
  }

  return `${first} ${last}`.trim();
};

/**
 * The given name alone, for greeting someone by it.
 *
 * Not `user.firstName`: the same Azure SSO quirk {@link formatDisplayName}
 * handles applies here, so a trailing comma means the fields are swapped and the
 * given name is in `lastName`. Falls back to the other field rather than
 * greeting nobody.
 */
export const formatFirstName = (
  firstName: string | undefined | null,
  lastName: string | undefined | null,
): string => {
  const first = firstName?.trim() ?? '';
  const last = lastName?.trim() ?? '';

  return (first.endsWith(',') ? last : first) || last;
};

/**
 * The single letter shown in an avatar circle. One letter for everyone, per
 * design.
 *
 * Via {@link formatFirstName} for the Azure SSO swap — `firstName: "Smith,"`
 * means the given name is in `lastName`.
 *
 * Not `meeting-secretary`'s `getInitials`: that takes a joined name plus an
 * email and returns two letters. No email here — see `ProjectPerson` in
 * `@consigli/types`.
 *
 * Code points, not `charAt`, which halves a surrogate pair. Combining marks
 * still split.
 */
export const formatInitials = (
  firstName: string | undefined | null,
  lastName: string | undefined | null,
): string => [...formatFirstName(firstName, lastName)].at(0)?.toUpperCase() ?? '';
